// usePinFlow.ts
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFirmwareContext } from '../../contexts/FirmwareContext';

export enum PinFlowStep {
  CurrentPin = 1,
  NewPin = 2,
  ConfirmPin = 3,
}

interface UsePinFlowProps {
  type: 'set' | 'reset' | 'disable';
  currentPin?: string[];
  onComplete: () => void;
}

export const usePinFlow = ({ type, currentPin = [], onComplete }: UsePinFlowProps) => {
  const { t } = useTranslation();
  const { setLockPin } = useFirmwareContext();
  const [step, setStep] = useState<PinFlowStep>(
    type === 'reset' || type === 'disable' ? PinFlowStep.CurrentPin : PinFlowStep.NewPin,
  );
  const [pin, setPin] = useState(['', '', '', '']);
  const [newPin, setNewPin] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const isSaveEnabled =
    step === PinFlowStep.ConfirmPin &&
    pin.every((d) => d !== '') &&
    pin.length === 4 &&
    newPin &&
    pin.join('') === newPin.join('');

  const handleSuccess = () => {
    if (type === 'reset' && step === PinFlowStep.CurrentPin) {
      if (pin.join('') === currentPin.join('')) {
        setStep(PinFlowStep.NewPin);
        setPin(['', '', '', '']);
        setError(null);
      } else {
        setError(t('settings.lockScreen.incorrectCurrentPin'));
        setPin(['', '', '', '']);
      }
    } else if (step === PinFlowStep.NewPin) {
      setNewPin(pin);
      setPin(['', '', '', '']);
      setStep(PinFlowStep.ConfirmPin);
      setError(null);
    } else if (step === PinFlowStep.ConfirmPin) {
      if (!(newPin && pin.join('') === newPin.join(''))) {
        setError(t('settings.lockScreen.pinMismatch'));
      } else {
        setError(null);
      }
    }
  };

  const handleSave = () => {
    if (isSaveEnabled) {
      setShowModal(true);
    }
  };

  const handleConfirmSave = async () => {
    try {
      if (type === 'disable') {
        await setLockPin(null);
      } else if (newPin) {
        await setLockPin(newPin.join(''));
      }
      onComplete();
    } catch {
      setError('Failed to save PIN');
    }
  };

  let validPin: string[] | null = null;
  if (step === PinFlowStep.CurrentPin) validPin = currentPin;
  if (step === PinFlowStep.ConfirmPin) validPin = newPin;

  useEffect(() => {
    if (type === 'disable' && pin.every((d) => d !== '') && pin.length === 4) {
      if (pin.join('') === currentPin.join('')) {
        setShowModal(true);
      } else {
        setError('Incorrect PIN code');
        setPin(['', '', '', '']);
      }
    }
  }, [pin, type, currentPin]);

  useEffect(() => {
    if (step === PinFlowStep.ConfirmPin && !newPin) {
      setStep(PinFlowStep.NewPin);
    }
  }, [step, newPin]);

  return {
    step,
    pin,
    setPin,
    error,
    isSaveEnabled,
    showModal,
    setShowModal,
    handleSuccess,
    handleSave,
    handleConfirmSave,
    validPin,
  };
};
